import { useState, type FormEvent } from 'react'
import { api } from '../lib/api'
import type { AskAnythingMessage } from '../lib/types'

type Props = {
  variant: 'hero' | 'dashboard'
  messages?: AskAnythingMessage[]
  onMessagesChange?: (messages: AskAnythingMessage[]) => void
}

export default function AskAnythingBar({ variant, messages: controlledMessages, onMessagesChange }: Props) {
  const [localMessages, setLocalMessages] = useState<AskAnythingMessage[]>([])
  const messages = controlledMessages ?? localMessages
  const setMessages = onMessagesChange ?? setLocalMessages

  const [value, setValue] = useState('')
  const [open, setOpen] = useState(false)
  const [expanded, setExpanded] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const isHero = variant === 'hero'

  async function handleSubmit(e: FormEvent) {
    e.preventDefault()
    const question = value.trim()
    if (!question || loading) return

    const history = messages
    const next = [...history, { role: 'user' as const, text: question }]
    setValue('')
    setError(null)
    setOpen(true)
    setMessages(next)
    setLoading(true)
    try {
      const { answer } = await api.askAnything(question, history)
      setMessages([...next, { role: 'assistant' as const, text: answer }])
    } catch {
      setError('Could not get an answer. Try again in a moment.')
    } finally {
      setLoading(false)
    }
  }

  // Dashboard variant starts as a floating icon and only becomes the bar + chat
  // panel once clicked, so it doesn't permanently occupy header space.
  if (!isHero && !expanded) {
    return (
      <button
        type="button"
        onClick={() => setExpanded(true)}
        aria-label="Ask anything"
        className="fixed bottom-20 right-6 z-50 w-14 h-14 rounded-full bg-force-green text-force-text shadow-lg flex items-center justify-center hover:bg-force-green-light transition-colors md:bottom-6"
      >
        <span className="material-symbols-outlined text-2xl">chat</span>
      </button>
    )
  }

  const pillClass = isHero
    ? 'flex items-center gap-2 bg-force-surface-2 border border-force-border-light rounded-full pl-4 pr-2 py-2 focus-within:border-force-green-light transition-colors'
    : 'flex items-center gap-2 bg-dash-surface-2 border border-dash-border rounded-full pl-4 pr-2 py-2 focus-within:border-force-green transition-colors shadow-lg'

  const inputClass = isHero
    ? 'flex-1 bg-transparent outline-none font-body-md text-body-md text-force-text placeholder:text-force-text-muted min-w-0'
    : 'flex-1 bg-transparent outline-none font-body-md text-body-md text-dash-text placeholder:text-dash-text-muted/60 min-w-0'

  const submitClass = isHero
    ? 'shrink-0 w-9 h-9 rounded-full bg-force-green text-force-text flex items-center justify-center hover:bg-force-green-light transition-colors disabled:opacity-50'
    : 'shrink-0 w-9 h-9 rounded-full bg-force-green text-force-text flex items-center justify-center hover:bg-force-green-light transition-colors disabled:opacity-50'

  const panelClass = isHero
    ? 'mb-2 max-h-64 overflow-y-auto rounded-xl border border-force-border-light bg-force-surface-2 p-3 flex flex-col gap-2'
    : 'mb-2 max-h-64 overflow-y-auto rounded-xl border border-dash-border bg-dash-surface-2 p-3 flex flex-col gap-2 shadow-lg'

  return (
    <div
      className={
        isHero
          ? 'w-full max-w-lg'
          : 'fixed bottom-20 right-6 z-50 w-[calc(100vw-3rem)] max-w-sm flex flex-col gap-2 md:bottom-6'
      }
    >
      {!isHero && (
        <button
          type="button"
          onClick={() => setExpanded(false)}
          aria-label="Close"
          className="self-end w-8 h-8 rounded-full bg-dash-surface-2 border border-dash-border text-dash-text-muted hover:bg-dash-surface-3 flex items-center justify-center shadow-lg transition-colors"
        >
          <span className="material-symbols-outlined text-lg">close</span>
        </button>
      )}

      {open && messages.length > 0 && (
        <div className={panelClass}>
          {messages.map((m, i) => (
            <div
              key={i}
              className={`font-body-md text-body-md rounded-lg px-3 py-2 max-w-[85%] ${
                m.role === 'user'
                  ? isHero
                    ? 'self-end bg-force-green/20 text-force-text'
                    : 'self-end bg-dash-accent-soft text-force-green-dark'
                  : isHero
                    ? 'self-start bg-black/25 text-force-text-muted'
                    : 'self-start bg-dash-surface-3 text-dash-text-muted'
              }`}
            >
              {m.text}
            </div>
          ))}
          {loading && (
            <p className={`font-caption text-caption ${isHero ? 'text-force-text-muted' : 'text-dash-text-muted'}`}>
              Thinking…
            </p>
          )}
          {error && <p className="font-caption text-caption text-red-400">{error}</p>}
        </div>
      )}

      <form onSubmit={handleSubmit} className={pillClass}>
        <span className={`material-symbols-outlined text-lg ${isHero ? 'text-force-text-muted' : 'text-dash-text-muted'}`}>
          mic
        </span>
        <input
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onFocus={() => messages.length > 0 && setOpen(true)}
          placeholder="Ask anything…"
          className={inputClass}
          autoFocus={!isHero}
        />
        {messages.length > 0 && (
          <button
            type="button"
            onClick={() => setOpen((o) => !o)}
            aria-label={open ? 'Collapse conversation' : 'Expand conversation'}
            className={`shrink-0 w-8 h-8 rounded-full flex items-center justify-center transition-colors ${
              isHero ? 'text-force-text-muted hover:bg-white/5' : 'text-dash-text-muted hover:bg-dash-surface-3'
            }`}
          >
            <span className="material-symbols-outlined text-lg">{open ? 'expand_more' : 'expand_less'}</span>
          </button>
        )}
        <button type="submit" aria-label="Ask" disabled={loading || !value.trim()} className={submitClass}>
          <span className="material-symbols-outlined text-lg">{loading ? 'hourglass_top' : 'send'}</span>
        </button>
      </form>
    </div>
  )
}
